import { MoreHorizontal } from 'lucide-react';
import { WarningMessage } from '@common/components';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '../ui/button';
import {
  useMutateCopyProject,
  useMutateDeleteTellet,
  useMutateMoveProject,
} from '@/reactQuery/post';
import useTranslation from '@/hooks/useTranslation';
import Routes from '@/router/routes';
import { AlertDialogTrigger } from '../ui/alert-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import RenameTelletModal from '../Modals/RenameTelletModal';
import UnpublishProjectModal from '../Modals/UnpublishProjectModal';
import { EProjectStatus } from '@/enums';
import { ActionProps, CopyProjectFormData, MoveProjectFormData } from '@/types';
import { showToastErrorMessage } from '@/utils/showToastErrorMessage';

function WorkspaceProjectActionsDD({
  mergedWorkspaces,
  targettedProjectID,
  isUserOwner,
  isUserEditor,
  projectTitle,
  projectStatus,
}: ActionProps) {
  const { organization_id: organizationId, workspace_id: workspaceId } = useParams();
  const { mutateAsync } = useMutateMoveProject(organizationId, workspaceId);
  const { mutateAsync: mutateAsyncCopyProject } = useMutateCopyProject(organizationId, workspaceId);
  const { mutateAsync: mutateAsyncDeleteTellet, isPending: isDeletingProjectPending } =
    useMutateDeleteTellet(organizationId, workspaceId);
  const [openDeletingModal, setOpenDeletingModal] = useState(false);
  const [openUnpublishModal, setOpenUnpublishModal] = useState(false);
  const { lang } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleMutation = async <F,>(
    mutationFn: (data: F) => Promise<unknown>,
    data: F,
    successMsg: string,
  ) => {
    try {
      await mutationFn(data);
      toast.success(lang.get(successMsg));
    } catch (err) {
      showToastErrorMessage(err);
    }
  };

  const handleDeleteTellet = () =>
    handleMutation(mutateAsyncDeleteTellet, { targettedProjectID }, 'msg.projectDeleted');

  const moveProject = (formData: MoveProjectFormData) =>
    handleMutation(mutateAsync, formData, 'msg.projectSuccessfullyMoved');

  const copyProject = (formData: CopyProjectFormData) =>
    handleMutation(mutateAsyncCopyProject, formData, 'msg.projectSuccessfullyCopied');

  const isUserEditorOrOwner = isUserOwner || isUserEditor;
  const isProjectPublishedOrCompleted =
    projectStatus === EProjectStatus.PUBLISHED || projectStatus === EProjectStatus.COMPLETED;
  const isProjectPublished = projectStatus === EProjectStatus.PUBLISHED;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="w-8 h-8 p-0">
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-52 z-100" side="left" align="center" sideOffset={8}>
        <DropdownMenuGroup>
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() =>
              navigate(`${pathname}${Routes.project_design.path}/${targettedProjectID}`)
            }
          >
            {lang.get('msg.view')}
          </DropdownMenuItem>
          {/* rename */}
          {isUserEditorOrOwner && (
            <RenameTelletModal projectId={targettedProjectID} projectTitle={projectTitle} />
          )}
          {/* TELV1-531 — duplicate into THIS workspace for the next tracker
              wave: questions/UUIDs, stimuli, coding frames and the embed
              generator config all carry over; fieldwork counters reset. */}
          {isUserEditorOrOwner && (
            <DropdownMenuItem
              onClick={() =>
                copyProject({
                  projectID: targettedProjectID,
                  targetWorkspaceId: workspaceId as string,
                })
              }
              className="flex items-center py-2 pl-2 cursor-pointer pr-7"
            >
              {lang.get('msg.duplicateForNextWave')}
            </DropdownMenuItem>
          )}
          {/* copy to */}
          {isUserEditorOrOwner && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>{lang.get('msg.copyTo')}</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="max-h-[50dvh] overflow-y-auto">
                  {mergedWorkspaces?.map((item) => (
                    <DropdownMenuItem
                      key={item?._id}
                      onClick={() =>
                        copyProject({
                          projectID: targettedProjectID,
                          targetWorkspaceId: item?._id,
                        })
                      }
                      className={`flex items-start py-2 pl-2 cursor-pointer pr-7
                       ${item?._id === workspaceId && 'bg-neutral-100'}`}
                    >
                      {item?.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
              {/* move to */}
              {isUserEditorOrOwner && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>{lang.get('msg.moveTo')}</DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent className="max-h-[50dvh]  overflow-y-auto">
                      {mergedWorkspaces?.map((item) => (
                        <DropdownMenuItem
                          key={item?._id}
                          onClick={() =>
                            !(item?._id === workspaceId) &&
                            moveProject({
                              projectID: targettedProjectID,
                              newWorkspaceId: item?._id,
                              oldWorkspaceId: workspaceId,
                            })
                          }
                          className={`flex items-start py-2 pl-2 cursor-pointer pr-7
                       ${item?._id === workspaceId && 'bg-neutral-100'}`}
                        >
                          {item?.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              )}
            </DropdownMenuSub>
          )}
        </DropdownMenuGroup>
        {/* Unpublish - only show for published projects and editors/owners */}
        {isUserEditorOrOwner && isProjectPublished && (
          <DropdownMenuItem
            className="flex w-full px-2 py-1 mt-1 text-sm text-orange-600 border-t cursor-pointer hover:bg-gray-100"
            onClick={() => setOpenUnpublishModal(true)}
          >
            {lang.get('msg.unpublish')}
          </DropdownMenuItem>
        )}
        {isUserEditorOrOwner && (
          <AlertDialog open={openDeletingModal}>
            <AlertDialogTrigger
              className="flex w-full px-2 py-1 mt-1 text-sm text-red-600 border-t rounded-sm hover:bg-gray-100"
              onClick={() => setOpenDeletingModal(true)}
            >
              {lang.get('msg.delete')}
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{lang.get('msg.areYouSure')}</AlertDialogTitle>
                <AlertDialogDescription className="text-dark-text">
                  {lang.get('msg.projectDeleteWarningPrefix')}
                  <span className="font-bold">{projectTitle}</span>
                  {lang.get('msg.projectDeleteWarningSuffix')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              {isProjectPublishedOrCompleted && (
                <WarningMessage>
                  {lang.get('msg.completedOrPublishedProjectDeleteWarning')}
                </WarningMessage>
              )}
              <AlertDialogFooter className="mt-5">
                <AlertDialogCancel onClick={() => setOpenDeletingModal(false)}>
                  {lang.get('msg.cancel')}
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={isDeletingProjectPending}
                  onClick={() => {
                    setOpenDeletingModal(false);
                    handleDeleteTellet();
                  }}
                  className="bg-red-600 hover:bg-red-900"
                >
                  {lang.get('msg.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </DropdownMenuContent>
      {/* UnpublishProjectModal must be outside DropdownMenuContent to prevent closing when dropdown closes */}
      {isUserEditorOrOwner && isProjectPublished && (
        <UnpublishProjectModal
          projectId={targettedProjectID}
          projectTitle={projectTitle}
          organizationId={organizationId}
          workspaceId={workspaceId}
          isOpen={openUnpublishModal}
          onClose={() => setOpenUnpublishModal(false)}
        />
      )}
    </DropdownMenu>
  );
}

export default WorkspaceProjectActionsDD;
